import { RefreshCw } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';

import { ProductsGeographyView } from '../components/ProductsGeographyView';
import {
  TEAL,
  TYPES,
  TYPE_META,
  type TypeStats,
  fmt,
  isActive,
  isBlocked,
  isReady,
  isSkipped,
  statsOf,
} from '../logic';

import type { ProductCompletenessRow, ProductType } from '@utazzo/shared';

import { useProductsBreakdown } from '@/features/dashboard/use-dashboard';

/**
 * Admin Products (catalog) dashboard. "Ready to quote" health across the six
 * masters - activities, transfers, hotels, cruises, visas, insurance. Grounded
 * in the itinerary pricing path: a product is ready when it has a live priced
 * rate (date-valid for hotels / cruises) and a geography to surface under.
 * Approval and image are reported but never gate readiness (the pickers ignore
 * them for these types). Every number is derived from one read
 * (`useProductsBreakdown`); nothing is hard-coded. A second tab breaks the same
 * rows down by geography (ProductsGeographyView).
 */

const SCOPED_CSS = `
.pd-root{color:#17171a;font-variant-numeric:tabular-nums}
.pd-root a{color:${TEAL};text-decoration:none;font-weight:600}
.pd-root a:hover{text-decoration:underline}
.pd-card{background:#fff;border:1px solid #e3e5e5;border-radius:14px;box-shadow:0 1px 2px rgba(9,30,40,.04)}
.pd-root table{border-collapse:separate;border-spacing:0;width:100%;min-width:720px}
.pd-root th{position:sticky;top:0;z-index:1;background:#f8f9f9;border-bottom:1px solid #e3e5e5;padding:9px 12px;font-size:11px;font-weight:700;color:#8a9095;letter-spacing:.02em;text-align:center;white-space:nowrap}
.pd-root th:first-child{text-align:left}
.pd-root td{border-bottom:1px solid #eef0f0;padding:9px 12px;font-size:13px;text-align:center;white-space:nowrap}
.pd-root td:first-child{text-align:left}
.pd-root tr:hover td{background:#fafbfb}
.pd-pill{font-size:12px;font-weight:600;padding:4px 10px;border-radius:999px;background:#fff;border:1px solid #e3e5e5;color:#17171a;cursor:pointer;display:inline-flex;gap:6px;align-items:center;line-height:1.3}
.pd-pill:hover{border-color:#c7cccf;background:#f6f7f8}
.pd-pill.on{background:${TEAL};color:#fff;border-color:${TEAL}}
.pd-pill.on:hover{background:#0d2f3c}
.pd-pill .n{font-weight:800;opacity:.6}
.pd-pill.on .n{opacity:.8;color:#cfeaf3}
.pd-tag{font-size:11px;font-weight:700;border-radius:6px;padding:2px 8px;display:inline-block}
`;

const Chip = ({ bg, fg, children }: { bg: string; fg: string; children: React.ReactNode }) => (
  <span className="pd-tag" style={{ background: bg, color: fg }}>
    {children}
  </span>
);
const Yes = () => <span style={{ color: '#047857', fontWeight: 700 }}>&#10003;</span>;
const Dash = () => <span style={{ color: '#c5c9cb' }}>&ndash;</span>;

type RowFilter = 'all' | 'ready' | 'blocked' | 'skipped' | 'price' | 'approval' | 'image';

const BreakdownTable = ({ type, rows }: { type: ProductType; rows: ProductCompletenessRow[] }) => {
  const meta = TYPE_META[type];
  const [filter, setFilter] = React.useState<RowFilter>('blocked');
  const [search, setSearch] = React.useState('');
  const term = search.trim().toLowerCase();

  const filterPred =
    (f: RowFilter) =>
    (r: ProductCompletenessRow): boolean => {
      if (f === 'all') return true;
      if (f === 'ready') return isActive(r) && isReady(r, type);
      // "Not ready" is short-haul only; long-haul unready goes to "Skipped".
      if (f === 'blocked') return isBlocked(r, type);
      if (f === 'skipped') return isSkipped(r, type);
      // Diagnostic filters stay scoped to short-haul so they line up with blocked.
      if (f === 'price') return isActive(r) && !r.priced && !r.isLongHaul;
      if (f === 'approval') return isActive(r) && !r.approved;
      return isActive(r) && !r.hasImage;
    };
  const count = (f: RowFilter): number => rows.filter((r) => filterPred(f)(r)).length;

  const visible = rows.filter((r) => {
    if (term && !r.title.toLowerCase().includes(term)) return false;
    return filterPred(filter)(r);
  });

  const hasSkipped = rows.some((r) => isSkipped(r, type));
  const pills: { key: RowFilter; label: string; missing?: boolean }[] = [
    { key: 'all', label: 'All' },
    { key: 'ready', label: 'Ready' },
    { key: 'blocked', label: 'Not ready' },
    ...(hasSkipped ? [{ key: 'skipped' as RowFilter, label: 'Skipped (long-haul)' }] : []),
    { key: 'price', label: 'No live price', missing: true },
    { key: 'approval', label: 'Unapproved', missing: true },
    ...(meta.showImage ? [{ key: 'image' as RowFilter, label: 'No image', missing: true }] : []),
  ];
  const missingStart = hasSkipped ? 4 : 3;

  return (
    <div className="pd-card" style={{ overflow: 'hidden' }}>
      <div
        style={{
          padding: '14px 16px',
          borderBottom: '1px solid #eef0f0',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          justifyContent: 'space-between',
        }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{meta.label} - ready to quote</div>
          <div style={{ fontSize: 12, color: '#6b7075', maxWidth: 640 }}>
            Ready when it has a live priced rate
            {type === 'hotels' || type === 'cruises' ? ' (date-valid for travel)' : ''} and a{' '}
            {meta.geoLabel.toLowerCase()} to surface under. Approval
            {meta.showImage ? ' and image are' : ' is'} quality-only and never blocks a quote.
            {hasSkipped
              ? ' Long-haul products (Europe, Japan, South Korea, Sri Lanka) that are not ready are skipped, not counted as blocked, until that catalog is fully managed.'
              : ''}
          </div>
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search ${meta.label.toLowerCase()}…`}
          style={{
            fontSize: 13,
            padding: '6px 10px',
            borderRadius: 8,
            border: '1px solid #e3e5e5',
            minWidth: 180,
          }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          alignItems: 'center',
          padding: '10px 16px',
          borderBottom: '1px solid #eef0f0',
        }}
      >
        {pills.map((p, i) => (
          <React.Fragment key={p.key}>
            {i === missingStart && (
              <>
                <span style={{ width: 1, height: 18, background: '#e3e5e5', margin: '0 3px' }} />
                <span style={{ fontSize: 12, color: '#6b7075' }}>Missing:</span>
              </>
            )}
            <button
              type="button"
              className={`pd-pill ${filter === p.key ? 'on' : ''}`}
              onClick={() => setFilter(p.key)}
            >
              {p.label} <span className="n">{fmt(count(p.key))}</span>
            </button>
          </React.Fragment>
        ))}
      </div>
      <div style={{ maxHeight: 460, overflow: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>{meta.label.replace(/s$/, '')}</th>
              <th>Status</th>
              <th>{meta.geoLabel}</th>
              <th>{meta.priceLabel}</th>
              {meta.showImage && <th>Image</th>}
              <th>Approved</th>
              <th>Ready</th>
            </tr>
          </thead>
          <tbody>
            {visible.slice(0, 500).map((r) => (
              <tr key={r.id}>
                <td>
                  <Link to={`${meta.route}/${r.id}/edit`} target="_blank" rel="noopener noreferrer">
                    {r.title}
                  </Link>
                </td>
                <td>
                  {r.status === 'ACTIVE' ? (
                    <Chip bg="#e7f6ef" fg="#047857">
                      Published
                    </Chip>
                  ) : r.status === 'DRAFT' ? (
                    <Chip bg="#f1f3f4" fg="#6b7075">
                      Draft
                    </Chip>
                  ) : (
                    <Chip bg="#fef3c7" fg="#b45309">
                      {r.status}
                    </Chip>
                  )}
                </td>
                <td>
                  {r.hasGeo ? (
                    (r.geo ?? <Yes />)
                  ) : r.isLongHaul ? (
                    <Chip bg="#fef3c7" fg="#b45309">
                      Missing
                    </Chip>
                  ) : (
                    <Chip bg="#fef3c7" fg="#b45309">
                      Missing
                    </Chip>
                  )}
                </td>
                <td>
                  {r.priced ? (
                    <Yes />
                  ) : r.isLongHaul ? (
                    <Chip bg="#fef3c7" fg="#b45309">
                      No price
                    </Chip>
                  ) : (
                    <Chip bg="#fde8ec" fg="#be123c">
                      No price
                    </Chip>
                  )}
                </td>
                {meta.showImage && <td>{r.hasImage ? <Yes /> : <Dash />}</td>}
                <td>{r.approved ? <Yes /> : <Dash />}</td>
                <td>
                  {isReady(r, type) ? (
                    <Chip bg="#e7f6ef" fg="#047857">
                      Ready
                    </Chip>
                  ) : r.isLongHaul ? (
                    <Chip bg="#fef3c7" fg="#b45309">
                      Skipped
                    </Chip>
                  ) : (
                    <Chip bg="#fde8ec" fg="#be123c">
                      Not ready
                    </Chip>
                  )}
                </td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr>
                <td
                  colSpan={meta.showImage ? 7 : 6}
                  style={{ color: '#8a9095', padding: '28px 12px' }}
                >
                  No {meta.label.toLowerCase()} match.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {visible.length > 500 && (
        <div
          style={{
            padding: '8px 16px',
            fontSize: 11,
            color: '#8a9095',
            borderTop: '1px solid #eef0f0',
          }}
        >
          Showing first 500 of {fmt(visible.length)} - narrow with search or a filter.
        </div>
      )}
    </div>
  );
};

export const ProductsDashboardPage = (): React.JSX.Element => {
  const query = useProductsBreakdown();
  const data = query.data;
  const [tab, setTab] = React.useState<ProductType>('activities');
  const [mode, setMode] = React.useState<'product' | 'geography'>('product');

  const stats = React.useMemo(() => {
    if (!data) return null;
    const per = {} as Record<ProductType, TypeStats>;
    for (const t of TYPES) per[t] = statsOf(data[t], t);
    const sum = (k: keyof TypeStats) => TYPES.reduce((n, t) => n + per[t][k], 0);
    return {
      per,
      totalActive: sum('active'),
      totalReady: sum('ready'),
      totalBlocked: sum('blocked'),
      totalSkipped: sum('skipped'),
      totalUnapproved: sum('unapproved'),
    };
  }, [data]);

  // In-scope = active minus the parked long-haul "skipped" set, so the health
  // ratios reflect only the catalog we actually manage today.
  const inScope = stats ? stats.totalActive - stats.totalSkipped : 0;
  const readyPct = stats && inScope > 0 ? Math.round((stats.totalReady / inScope) * 100) : 0;
  const blockedPct = stats && inScope > 0 ? Math.round((stats.totalBlocked / inScope) * 100) : 0;

  return (
    <div
      className="pd-root"
      style={{ background: '#f6f7f8', minHeight: '100%', padding: '18px 20px' }}
    >
      <style>{SCOPED_CSS}</style>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 10,
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: '-.02em' }}>
            Products dashboard
          </h1>
          <p style={{ margin: '3px 0 0', fontSize: 13, color: '#6b7075' }}>
            Every catalog product and whether it can actually be quoted in an itinerary -
            activities, transfers, hotels, cruises, visas, insurance.
          </p>
        </div>
        <button
          type="button"
          onClick={() => query.refetch()}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            fontWeight: 600,
            padding: '6px 12px',
            borderRadius: 999,
            border: '1px solid #e3e5e5',
            background: '#fff',
            cursor: 'pointer',
            color: '#17171a',
          }}
        >
          <RefreshCw size={13} className={query.isFetching ? 'animate-spin' : undefined} /> Refresh
        </button>
      </div>

      {data && (
        <div style={{ display: 'flex', gap: 6, marginTop: 14 }}>
          <button
            type="button"
            className={`pd-pill ${mode === 'product' ? 'on' : ''}`}
            onClick={() => setMode('product')}
          >
            By product
          </button>
          <button
            type="button"
            className={`pd-pill ${mode === 'geography' ? 'on' : ''}`}
            onClick={() => setMode('geography')}
          >
            By geography
          </button>
        </div>
      )}

      {query.isLoading && (
        <div style={{ padding: 40, textAlign: 'center', color: '#8a9095' }}>
          Loading catalog health&hellip;
        </div>
      )}
      {query.isError && (
        <div style={{ padding: 40, textAlign: 'center', color: '#be123c' }}>
          Could not load the products dashboard.
        </div>
      )}

      {data && mode === 'geography' && <ProductsGeographyView data={data} />}

      {data && stats && mode === 'product' && (
        <>
          {/* KPIs */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 12,
              marginTop: 18,
            }}
          >
            <div
              className="pd-card"
              style={{
                padding: '16px 18px',
                background: TEAL,
                borderColor: 'transparent',
                color: '#e8f6fb',
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 700, color: '#9fcad9' }}>Ready to quote</div>
              <div style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-.03em', marginTop: 6 }}>
                {fmt(stats.totalReady)}{' '}
                <span style={{ fontSize: 13, fontWeight: 600, color: '#8fb6c6' }}>
                  of {fmt(inScope)} in scope
                </span>
              </div>
              <div
                style={{
                  height: 6,
                  borderRadius: 6,
                  background: 'rgba(255,255,255,.18)',
                  overflow: 'hidden',
                  marginTop: 10,
                }}
              >
                <div style={{ width: `${readyPct}%`, height: '100%', background: '#5fd0a0' }} />
              </div>
              {stats.totalSkipped > 0 && (
                <div style={{ fontSize: 11, color: '#8fb6c6', marginTop: 8 }}>
                  + {fmt(stats.totalSkipped)} long-haul skipped (not counted yet)
                </div>
              )}
            </div>

            <div className="pd-card" style={{ padding: '16px 18px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7075' }}>
                No live price - can&rsquo;t quote
              </div>
              <div
                style={{
                  fontSize: 32,
                  fontWeight: 800,
                  letterSpacing: '-.03em',
                  color: '#be123c',
                  marginTop: 6,
                }}
              >
                {fmt(stats.totalBlocked)}{' '}
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    background: '#fde8ec',
                    color: '#be123c',
                    borderRadius: 999,
                    padding: '2px 8px',
                  }}
                >
                  {blockedPct}% of published
                </span>
              </div>
              <div style={{ fontSize: 11, color: '#6b7075', marginTop: 8 }}>
                Short-haul published with no priced rate (or expired) &rarr; silently quotes
                &#8377;0.
                {stats.totalSkipped > 0
                  ? ` Long-haul gaps (${fmt(stats.totalSkipped)}) are skipped.`
                  : ''}
              </div>
            </div>

            <div className="pd-card" style={{ padding: '16px 18px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7075' }}>
                Awaiting approval{' '}
                <span style={{ color: '#8a9095', fontWeight: 600 }}>&middot; non-blocking</span>
              </div>
              <div
                style={{
                  fontSize: 28,
                  fontWeight: 800,
                  letterSpacing: '-.03em',
                  color: '#0e7490',
                  marginTop: 6,
                }}
              >
                {fmt(stats.totalUnapproved)}
              </div>
              <div style={{ fontSize: 11, color: '#6b7075', marginTop: 8 }}>
                A catalog-quality signal; approval does not block itinerary use for these types.
              </div>
            </div>

            <div className="pd-card" style={{ padding: '16px 18px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7075' }}>
                Where to look next
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '6px 16px',
                  marginTop: 8,
                }}
              >
                {TYPES.map((t) => ({ t, n: stats.per[t].blocked }))
                  .filter((x) => x.n > 0)
                  .sort((a, b) => b.n - a.n)
                  .slice(0, 4)
                  .map((x) => (
                    <div key={x.t} style={{ fontSize: 12 }}>
                      <b style={{ fontSize: 15, color: '#be123c' }}>{fmt(x.n)}</b>
                      <div style={{ fontSize: 11, color: '#6b7075' }}>
                        {TYPE_META[x.t].label.toLowerCase()} blocked
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* Surface tiles */}
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '.08em',
              textTransform: 'uppercase',
              color: '#8a9095',
              margin: '24px 0 10px',
            }}
          >
            Surfaces{' '}
            <span
              style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500, color: '#a0a5a9' }}
            >
              - badge = published products that can&rsquo;t be quoted
            </span>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 12,
            }}
          >
            {TYPES.map((t) => {
              const s = stats.per[t];
              const m = TYPE_META[t];
              const scope = s.active - s.skipped;
              const okPct = scope > 0 ? Math.round((s.ready / scope) * 100) : 100;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => {
                    setTab(t);
                    document.getElementById('pd-breakdown')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="pd-card"
                  style={{
                    padding: 14,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 9,
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 6,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12.5,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 7,
                      }}
                    >
                      <span
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: 7,
                          display: 'grid',
                          placeItems: 'center',
                          fontSize: 12,
                          fontWeight: 800,
                          background: m.icBg,
                          color: m.icFg,
                        }}
                      >
                        {m.ic}
                      </span>
                      {m.label}
                    </span>
                    {s.blocked > 0 ? (
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: '#be123c',
                          background: '#fde8ec',
                          borderRadius: 999,
                          padding: '2px 7px',
                        }}
                      >
                        {fmt(s.blocked)} blocked
                      </span>
                    ) : s.skipped > 0 ? (
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: '#b45309',
                          background: '#fef3c7',
                          borderRadius: 999,
                          padding: '2px 7px',
                        }}
                      >
                        {fmt(s.skipped)} skipped
                      </span>
                    ) : (
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: '#047857',
                          background: '#e7f6ef',
                          borderRadius: 999,
                          padding: '2px 7px',
                        }}
                      >
                        All ready
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 25, fontWeight: 800, letterSpacing: '-.02em' }}>
                    {fmt(s.total)}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: '#6b7075',
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>Published {fmt(s.active)}</span>
                    <span>Draft {fmt(s.draft)}</span>
                  </div>
                  <div
                    style={{
                      height: 6,
                      borderRadius: 6,
                      background: '#e5e7e7',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${okPct}%`,
                        height: '100%',
                        background: okPct >= 70 ? '#047857' : '#be123c',
                      }}
                    />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Breakdown */}
          <div
            id="pd-breakdown"
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '.08em',
              textTransform: 'uppercase',
              color: '#8a9095',
              margin: '24px 0 10px',
            }}
          >
            Completeness breakdown
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
            {TYPES.map((t) => (
              <button
                key={t}
                type="button"
                className={`pd-pill ${tab === t ? 'on' : ''}`}
                onClick={() => setTab(t)}
              >
                {TYPE_META[t].label} <span className="n">{fmt(stats.per[t].blocked)}</span>
              </button>
            ))}
          </div>
          <BreakdownTable type={tab} rows={data[tab]} />
        </>
      )}
    </div>
  );
};
